import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { StaleIndicator } from "@/components/ui/status";
import { StaleState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { SectionTitle } from "@/features/oversight/oversight-ui";

/**
 * Source freshness matrix — the wallet × source observation grid.
 *
 * Extracted from `pages/enterprise/monitoring-freshness.tsx` (design-port
 * `oversight` lane 1, ALL-5825) so the two screens the prototype puts this grid
 * on — `/monitoring/freshness` and the `/reconciliation` overview's "Source
 * Freshness Matrix" heading — render one component rather than two. The move is
 * behaviour-preserving: same markup, classes, types and rule.
 *
 * Source freshness is observation data: it only exists once a source has
 * actually reported. Nothing here is derived, estimated or defaulted — a
 * wallet × source pair with no reported age renders as a dash rather than a
 * plausible-looking duration, and a scan that fails renders no matrix at all.
 *
 * Display only (no actions), so there is no page-spec action to bind
 * (`/monitoring/freshness` is carried as a route-spec baseline entry).
 */
export interface FreshnessCell {
  /** Age of the newest observation, as reported by the source, e.g. "42m ago". */
  age: string;
  /** True when the source is outside its freshness tolerance. */
  stale: boolean;
}

export interface FreshnessRow {
  wallet: string;
  balance: FreshnessCell;
  tx: FreshnessCell;
  event: FreshnessCell;
}

export type FreshnessState =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "error"; supportReference?: string | null }
  | { status: "ready"; rows: FreshnessRow[]; lastScan?: string | null };

const SOURCE_COLUMNS = [
  { key: "balance", label: "Balance Source" },
  { key: "tx", label: "TX Source" },
  { key: "event", label: "Event Source" },
] as const;

function SourceCell({ cell }: { cell: FreshnessCell }) {
  if (!cell.age) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-muted"
        title="The source did not report an observation time"
      >
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-line" />—
      </span>
    );
  }

  if (!cell.stale) {
    return <StaleIndicator stale={false} label={cell.age} />;
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant="warning" dot={false}>
        Stale
      </Badge>
      <span className="text-xs font-semibold tabular-nums text-warning">{cell.age}</span>
    </span>
  );
}

export function FreshnessMatrix({ rows, lastScan }: { rows: FreshnessRow[]; lastScan?: string | null }) {
  const sourceCount = rows.length * SOURCE_COLUMNS.length;
  const staleCount = rows.reduce(
    (n, row) => n + SOURCE_COLUMNS.filter((column) => row[column.key].stale).length,
    0,
  );
  // A source that never reported an observation is not evidence that it is
  // fresh. Counting those cells as "within tolerance" let a matrix of dashes
  // render an all-clear, so the banner is withheld until every cell carries an
  // observed age.
  const unreportedCount = rows.reduce(
    (n, row) => n + SOURCE_COLUMNS.filter((column) => !row[column.key].age).length,
    0,
  );

  return (
    <div>
      {staleCount > 0 ? (
        <StaleState
          title={`${staleCount} of ${sourceCount} sources are outside their freshness tolerance`}
          description="Affected cells show the age of their last observation. Records stay readable and operations continue on last confirmed values."
          lastSyncedAt={lastScan ? `Last full scan: ${lastScan}` : undefined}
        />
      ) : lastScan && unreportedCount === 0 ? (
        // The all-clear is only shown with a scan time to evidence it and only
        // when every source actually reported; without either, the page stays
        // silent rather than claiming a scan it cannot cite.
        <InfoBanner variant="info">
          {`All sources within freshness tolerance. Last full scan: ${lastScan}.`}
        </InfoBanner>
      ) : null}

      <SectionTitle>Freshness Matrix — Wallet × Source</SectionTitle>
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Wallet</th>
              {SOURCE_COLUMNS.map((column) => (
                <th key={column.key} className="px-4 py-2.5">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.wallet} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{row.wallet}</td>
                {SOURCE_COLUMNS.map((column) => (
                  <td key={column.key} className="px-4 py-3">
                    <SourceCell cell={row[column.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}
